/**
 * Материалы песочницы: из чего строятся горки, столы и тела.
 *
 * Числа — справочные порядки величин, а не замер конкретного образца: в песочнице важно,
 * что песок тормозит шар сильнее ткани, а лёд — слабее всех. Один список на все будущие
 * песочницы (тепловую, электрическую…): новые свойства добавляются сюда полями.
 *
 * Пара материалов в контакте:
 *  - трение и упругость удара — меньшее из двух (скользкое побеждает: брусок на льду скользит);
 *  - сопротивление качению — большее из двух (мягкое побеждает: шар вязнет в песке);
 *  - «идеально гладкая» поверхность воображаемая: с ней и трение, и качение — ноль.
 */

export interface Материал {
  id: string
  название: string
  /** Цвет на рисунке. */
  цвет: string
  /** кг/м³ — для массы по размеру в редакторе и для плавания в будущем. */
  плотность: number
  /** Коэффициент трения покоя: брусок стоит, пока сила не превысит μп·N. */
  трениеПокоя: number
  /** Коэффициент трения скольжения: всегда не больше трения покоя. */
  трениеСкольжения: number
  /**
   * Коэффициент сопротивления качению: катящийся шар или тележку тормозит сила c·N.
   * Именно она останавливает шар в опыте Галилея — шар не скользит, а катится.
   */
  качение: number
  /** Коэффициент восстановления при ударе: 0 — не отскакивает, 1 — отскакивает без потерь. */
  упругость: number
  /** Притягивается магнитом. */
  магнитный: boolean
  /** Воображаемый материал (мысленный опыт), подписывается как воображаемый. */
  воображаемый?: boolean
}

export const МАТЕРИАЛЫ: Record<string, Материал> = {
  дерево: { id: 'дерево', название: 'дерево', цвет: '#a97c50', плотность: 600, трениеПокоя: 0.5, трениеСкольжения: 0.3, качение: 0.006, упругость: 0.5, магнитный: false },
  сталь: { id: 'сталь', название: 'сталь', цвет: '#8d98a3', плотность: 7800, трениеПокоя: 0.4, трениеСкольжения: 0.3, качение: 0.002, упругость: 0.8, магнитный: true },
  стекло: { id: 'стекло', название: 'стекло', цвет: '#a9d3dc', плотность: 2500, трениеПокоя: 0.4, трениеСкольжения: 0.3, качение: 0.002, упругость: 0.7, магнитный: false },
  пластик: { id: 'пластик', название: 'пластик', цвет: '#6f9fd8', плотность: 1000, трениеПокоя: 0.4, трениеСкольжения: 0.3, качение: 0.005, упругость: 0.6, магнитный: false },
  резина: { id: 'резина', название: 'резина', цвет: '#4a4f57', плотность: 1200, трениеПокоя: 0.9, трениеСкольжения: 0.7, качение: 0.015, упругость: 0.8, магнитный: false },
  ткань: { id: 'ткань', название: 'ткань', цвет: '#7f8fb5', плотность: 300, трениеПокоя: 0.6, трениеСкольжения: 0.5, качение: 0.025, упругость: 0.2, магнитный: false },
  асфальт: { id: 'асфальт', название: 'асфальт', цвет: '#5e6266', плотность: 2300, трениеПокоя: 0.8, трениеСкольжения: 0.6, качение: 0.04, упругость: 0.4, магнитный: false },
  щебень: { id: 'щебень', название: 'щебень', цвет: '#9b958c', плотность: 1700, трениеПокоя: 0.8, трениеСкольжения: 0.7, качение: 0.12, упругость: 0.2, магнитный: false },
  песок: { id: 'песок', название: 'песок', цвет: '#ecd59a', плотность: 1600, трениеПокоя: 0.7, трениеСкольжения: 0.6, качение: 0.25, упругость: 0.05, магнитный: false },
  лёд: { id: 'лёд', название: 'лёд', цвет: '#cfe9f5', плотность: 900, трениеПокоя: 0.05, трениеСкольжения: 0.03, качение: 0.001, упругость: 0.3, магнитный: false },
  гладь: { id: 'гладь', название: 'идеально гладкая поверхность', цвет: '#e9eef2', плотность: 1000, трениеПокоя: 0, трениеСкольжения: 0, качение: 0, упругость: 0.5, магнитный: false, воображаемый: true },
}

export function материал(id: string): Материал {
  const м = МАТЕРИАЛЫ[id]
  if (!м) throw new Error(`Нет такого материала: ${id}`)
  return м
}

/** Коэффициенты пары материалов в контакте (правила — в шапке файла). */
export function пара(a: string, b: string): { покоя: number; скольжения: number; качение: number; упругость: number } {
  const ма = материал(a)
  const мб = материал(b)
  const гладко = ма.воображаемый || мб.воображаемый
  return {
    покоя: гладко ? 0 : Math.min(ма.трениеПокоя, мб.трениеПокоя),
    скольжения: гладко ? 0 : Math.min(ма.трениеСкольжения, мб.трениеСкольжения),
    качение: гладко ? 0 : Math.max(ма.качение, мб.качение),
    упругость: Math.min(ма.упругость, мб.упругость),
  }
}
